/**
 * The fonts a storefront can load: a closed catalog of OFL variable fonts, self-hosted from each
 * `@fontsource-variable/<id>` package and served from the storefront's own origin.
 *
 * A theme's font fields stay free text (any stack `safeThemeFontFamily` accepts): a stack whose
 * first family names a catalog font gets that font's files loaded, and any other stack renders
 * only where the shopper's device has it. Pure data, so server code reads it too; the imports that
 * load the files live in `@loot-os/ui/lib/storefrontFonts`, keyed by `id` (a test keeps the two
 * lists in step).
 */

/** How the dashboard's font picker groups the catalog, in this order. */
export const STOREFRONT_FONT_CATEGORIES = [
  { id: "sans", label: "Sans serif" },
  { id: "display", label: "Display" },
  { id: "serif", label: "Serif" },
  { id: "mono", label: "Monospace" },
] as const;
export type StorefrontFontCategory = (typeof STOREFRONT_FONT_CATEGORIES)[number]["id"];

/**
 * `id` is the package (`@fontsource-variable/<id>`); `name` is what a tenant types; `family` is
 * the exact name the package's `@font-face` rules register, which is what the page has to ask for;
 * `fallback` is the generic family the font's look falls back to while it loads.
 */
export const STOREFRONT_FONTS = [
  {
    id: "inter",
    name: "Inter",
    family: "Inter Variable",
    category: "sans",
    fallback: "sans-serif",
  },
  {
    id: "manrope",
    name: "Manrope",
    family: "Manrope Variable",
    category: "sans",
    fallback: "sans-serif",
  },
  {
    id: "dm-sans",
    name: "DM Sans",
    family: "DM Sans Variable",
    category: "sans",
    fallback: "sans-serif",
  },
  {
    id: "plus-jakarta-sans",
    name: "Plus Jakarta Sans",
    family: "Plus Jakarta Sans Variable",
    category: "sans",
    fallback: "sans-serif",
  },
  {
    id: "figtree",
    name: "Figtree",
    family: "Figtree Variable",
    category: "sans",
    fallback: "sans-serif",
  },
  {
    id: "geist",
    name: "Geist",
    family: "Geist Variable",
    category: "sans",
    fallback: "sans-serif",
  },
  {
    id: "outfit",
    name: "Outfit",
    family: "Outfit Variable",
    category: "sans",
    fallback: "sans-serif",
  },
  { id: "sora", name: "Sora", family: "Sora Variable", category: "sans", fallback: "sans-serif" },
  {
    id: "montserrat",
    name: "Montserrat",
    family: "Montserrat Variable",
    category: "sans",
    fallback: "sans-serif",
  },
  {
    id: "space-grotesk",
    name: "Space Grotesk",
    family: "Space Grotesk Variable",
    category: "display",
    fallback: "sans-serif",
  },
  {
    id: "bricolage-grotesque",
    name: "Bricolage Grotesque",
    family: "Bricolage Grotesque Variable",
    category: "display",
    fallback: "sans-serif",
  },
  {
    id: "oswald",
    name: "Oswald",
    family: "Oswald Variable",
    category: "display",
    fallback: "sans-serif",
  },
  {
    id: "playfair-display",
    name: "Playfair Display",
    family: "Playfair Display Variable",
    category: "serif",
    fallback: "serif",
  },
  {
    id: "fraunces",
    name: "Fraunces",
    family: "Fraunces Variable",
    category: "serif",
    fallback: "serif",
  },
  { id: "lora", name: "Lora", family: "Lora Variable", category: "serif", fallback: "serif" },
  {
    id: "jetbrains-mono",
    name: "JetBrains Mono",
    family: "JetBrains Mono Variable",
    category: "mono",
    fallback: "monospace",
  },
] as const satisfies readonly {
  id: string;
  name: string;
  family: string;
  category: StorefrontFontCategory;
  fallback: string;
}[];

export type StorefrontFont = (typeof STOREFRONT_FONTS)[number];
export type StorefrontFontId = StorefrontFont["id"];

/** The stack the page sets, and the catalog font it needs loaded (null = none). */
export interface LoadableFontStack {
  stack: string;
  font: StorefrontFont | null;
}

/** A family as written in a stack, compared without its quotes, case or doubled spaces. */
function familyKey(family: string): string {
  return family
    .trim()
    .replace(/^(["'])(.*)\1$/, "$2")
    .replace(/\s+/g, " ")
    .toLowerCase();
}

/**
 * A sanitized font stack as the page has to set it to get the catalog font it names first: the
 * family the loaded `@font-face` registers goes in front (`Manrope, sans-serif` →
 * `"Manrope Variable", Manrope, sans-serif`), and a stack that names nothing else gains the
 * font's fallback, so the text never shows in the browser's default serif while the file loads.
 * A first family the catalog does not have leaves the stack as it is, with no font to load.
 */
export function toLoadableFontStack(stack: string): LoadableFontStack {
  const families = stack.split(",");
  const first = familyKey(families[0] ?? "");
  const font =
    STOREFRONT_FONTS.find(
      (entry) => familyKey(entry.name) === first || familyKey(entry.family) === first,
    ) ?? null;
  if (!font) return { stack, font: null };

  const named = familyKey(font.family) === first ? stack : `"${font.family}", ${stack}`;
  return { stack: families.length === 1 ? `${named}, ${font.fallback}` : named, font };
}
